import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { onIdTokenChanged, signOut, type User } from "firebase/auth";
import { auth } from "../lib/config/firebaseInit";

type Claims = Record<string, unknown>;

interface UserContextValue {
  uid: string;
  email: string;
  user: User | null;
  token: string | null;
  claims: Claims;
  isAuthenticated: boolean;
  isLoading: boolean;
  sessionExpiresAt: number | null;
  isSessionExpired: boolean;
  logout: () => Promise<void>;
  // Kept for backward compatibility — Firebase auth state is the source of truth
  set_uid: (id: string) => void;
  set_email: (e: string) => void;
  set_claims: (c: Claims) => void;
}

const UserContext = createContext<UserContextValue | null>(null);

export function useUserContext() {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUserContext must be used within UserContextProvider");
  }
  return context;
}

export function UserContextProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [claims, setClaims] = useState<Claims>({});
  const [sessionExpiresAt, setSessionExpiresAt] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const logoutTimerRef = useRef<number | null>(null);

  const logout = useCallback(async () => {
    await signOut(auth);
  }, []);

  useEffect(() => {
    const unsubscribe = onIdTokenChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        const tokenResult = await firebaseUser.getIdTokenResult();
        setUser(firebaseUser);
        setToken(tokenResult.token);
        setClaims(tokenResult.claims as Claims);
        setSessionExpiresAt(new Date(tokenResult.expirationTime).getTime());
      } else {
        setUser(null);
        setToken(null);
        setClaims({});
        setSessionExpiresAt(null);
      }
      setIsLoading(false);
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    if (logoutTimerRef.current !== null) {
      window.clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }

    if (!user || !sessionExpiresAt) return;

    const remainingMs = sessionExpiresAt - Date.now();
    if (remainingMs <= 0) {
      logout();
      return;
    }

    logoutTimerRef.current = window.setTimeout(logout, remainingMs);

    return () => {
      if (logoutTimerRef.current !== null) {
        window.clearTimeout(logoutTimerRef.current);
        logoutTimerRef.current = null;
      }
    };
  }, [user, sessionExpiresAt, logout]);

  useEffect(() => {
    const validateSession = () => {
      if (user && sessionExpiresAt && sessionExpiresAt <= Date.now()) {
        logout();
      }
    };

    window.addEventListener("focus", validateSession);
    document.addEventListener("visibilitychange", validateSession);

    return () => {
      window.removeEventListener("focus", validateSession);
      document.removeEventListener("visibilitychange", validateSession);
    };
  }, [user, sessionExpiresAt, logout]);

  const value = useMemo<UserContextValue>(
    () => ({
      uid: user?.uid ?? "",
      email: user?.email ?? "",
      user,
      token,
      claims,
      isAuthenticated: !!user,
      isLoading,
      sessionExpiresAt,
      isSessionExpired: Boolean(sessionExpiresAt && sessionExpiresAt <= Date.now()),
      logout,
      set_uid: () => {},
      set_email: () => {},
      set_claims: setClaims,
    }),
    [user, token, claims, isLoading, sessionExpiresAt, logout]
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}
